import { getOr } from '@/utils/options';
import { Heading, Label, Subheading } from 'components/src/typography';
import * as React from 'react';
import { ExtraAddOnsFieldsFragment } from 'types/generated';
import AddOnCard from '../Card';
import { centeredMainGridClasses } from '@/styles/grid';
import { appendAddOnsAtom, addOnsQuantityAtom } from '@/store';
import { useSetAtom } from 'jotai';
import { AnimatePresence, motion } from 'framer-motion';
import SynCardCarousel from '../SynCardCarousel';

type Props = {
  data: ExtraAddOnsFieldsFragment;
};

const ExtraAddOns = ({ data }: Props) => {
  const appendAddOns = useSetAtom(appendAddOnsAtom);
  const setAddOnsQuantity = useSetAtom(addOnsQuantityAtom);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [animationStart, setAnimationStart] = React.useState({ x: 0, y: 0 });
  const [cartPosition, setCartPosition] = React.useState({ x: 0, y: 0 });

  const addToCart = (id: string) => {
    if (isAnimating) return;

    const cardElement = document.getElementById(id);

    if (cardElement) {
      const buttonRect = cardElement.getBoundingClientRect();
      const buttonX = buttonRect.left + buttonRect.width / 2;
      const buttonY = buttonRect.top + buttonRect.height / 2;

      setAnimationStart({ x: buttonX, y: buttonY });
      const cartElement = document.getElementById('cart-position');

      if (cartElement) {
        const cartRect = cartElement.getBoundingClientRect();
        setCartPosition({
          x: cartRect.left + cartRect.width / 2,
          y: cartRect.top + cartRect.height / 2,
        });
      }
    }

    setIsAnimating(true);

    setTimeout(() => {
      setAddOnsQuantity({ id: id, change: 1 });
      setIsAnimating(false);
    }, 500);

    const cartEvent = new CustomEvent('addToCart', { detail: { productId: Date.now() } });
    document.dispatchEvent(cartEvent);
  };

  React.useEffect(() => {
    const updateCartPosition = () => {
      const cartElement = document.getElementById('cart-position');

      if (cartElement) {
        const cartRect = cartElement.getBoundingClientRect();
        setCartPosition({
          x: cartRect.left + cartRect.width / 2,
          y: cartRect.top + cartRect.height / 2,
        });
      }
    };
    updateCartPosition();

    window.addEventListener('resize', updateCartPosition);
    const intervalId = setInterval(updateCartPosition, 1000);

    return () => {
      window.removeEventListener('resize', updateCartPosition);
      clearInterval(intervalId);
    };
  }, []);

  React.useEffect(() => {
    const addOnsToAdd = data.addOnCardsCollection?.items?.map((item) => ({
      id: item?.id,
      title: item?.title,
      price: item?.price,
      multiselect: Boolean(item?.multiselect),
    }));

    if (addOnsToAdd) {
      appendAddOns(addOnsToAdd);
    }
  }, [data.addOnCardsCollection, appendAddOns, data]);

  const addOnCards = data.addOnCardsCollection?.items;
  return (
    <div className={centeredMainGridClasses}>
      <div className="col-span-full md:-mx-16">
        <div className="flex flex-col justify-between py-24 md:flex-row">
          <div>
            {data.tagline && <Label classes="text-black-75">{data.tagline}</Label>}
            <Heading variant="medium" classes="text-black-10 mb-16">
              {getOr(data.title, '')}
            </Heading>
          </div>
          {data.description && (
            <div className="flex flex-col items-start md:justify-end">
              <Subheading bold classes="text-black-10 mb-16 max-w-[462px]">
                {data.description}
              </Subheading>
            </div>
          )}
        </div>

        {addOnCards && (
          <SynCardCarousel
            cards={addOnCards?.flatMap((card) =>
              card ? (
                <AddOnCard
                  card={card}
                  key={card?.id}
                  onClick={() => {
                    if (card.id) {
                      addToCart(card.id);
                    }
                  }}
                />
              ) : (
                []
              )
            )}
          />
        )}
      </div>
      <AnimatePresence>
        {isAnimating && (
          <motion.div
            className="card-elevation-1 fixed z-50 flex h-32 w-32 rounded-full bg-red-100"
            style={{
              top: 0,
              left: 0,
              marginLeft: '-12px',
              marginTop: '-12px',
            }}
            initial={{
              x: animationStart.x,
              y: animationStart.y,
              scale: 0,
            }}
            animate={{
              x: [animationStart.x, animationStart.x, cartPosition.x, cartPosition.x],
              y: [animationStart.y, animationStart.y, cartPosition.y, cartPosition.y],
              scale: [0, 1.2, 1, 0],
            }}
            transition={{ duration: 0.8, ease: 'easeInOut' }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ExtraAddOns;
